import React, { useMemo } from "react";
import { Skeleton } from "components/ui/skeleton";
import { LayoutGrid, Send, MailOpen, MousePointerClick, ArrowUpRight, ArrowDownRight, Plus } from "lucide-react";
import { EmptyState as SharedEmptyState } from "components/ui/empty-state";

/* ─────────── Sparkline SVG ─────────── */
const Sparkline: React.FC<{ data: number[]; color: string; height?: number }> = ({
    data, color, height = 24,
}) => {
    if (data.length < 2) return null;
    const max = Math.max(...data, 1);
    const min = Math.min(...data, 0);
    const range = max - min || 1;
    const w = 72;
    const pts = data.map((v, i) => {
        const x = (i / (data.length - 1)) * w;
        const y = height - ((v - min) / range) * (height - 6) - 3;
        return `${x},${y}`;
    });
    const firstPt = pts[0];
    const lastPt = pts[pts.length - 1];
    const areaPath = `M${firstPt} ${pts.slice(1).map(p => `L${p}`).join(" ")} L${lastPt.split(",")[0]},${height} L0,${height} Z`;
    return (
        <svg width={w} height={height} viewBox={`0 0 ${w} ${height}`} className="flex-shrink-0">
            <defs>
                <linearGradient id={`sg-${color.replace("#", "")}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.15" />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
            </defs>
            <path d={areaPath} fill={`url(#sg-${color.replace("#", "")})`} />
            <polyline fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" points={pts.join(" ")} />
            <circle cx={lastPt.split(",")[0]} cy={lastPt.split(",")[1]} r="2" fill={color} />
        </svg>
    );
};

interface KPICardProps {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    trend?: number | null;
    trendLabel: string;
    accentColor: string;
    accentHex: string;
    iconBg: string;
    iconColor: string;
    sparkData?: number[];
    isLoading?: boolean;
    animDelay?: number;
}

const KPICard: React.FC<KPICardProps> = ({
    title, value, icon, trend, trendLabel,
    accentColor, accentHex, iconBg, iconColor,
    sparkData = [], isLoading = false, animDelay = 0,
}) => {
    const hasPositiveTrend = (trend ?? 0) >= 0;
    // Only show sparkline when there's real variation in the data
    const hasSpark = sparkData.length > 1 && sparkData.some(v => v > 0);

    return (
        <div
            className="group relative overflow-hidden bg-surface dark:bg-dark-800 rounded-xl
                border border-line dark:border-dark-700
                shadow-sm hover:shadow-md hover:-translate-y-0.5
                transition-all duration-200 cursor-default"
            style={{ animationDelay: `${animDelay}ms` }}
        >
            {/* Accent top bar */}
            <div className={`absolute top-0 inset-x-0 h-[2px] ${accentColor}`} />

            <div className="px-4 pt-4 pb-3.5">

                {/* ── Row 1: icon + title ── */}
                <div className="flex items-center gap-2.5 mb-3">
                    {isLoading ? (
                        <Skeleton className="h-7 w-7 rounded-md flex-shrink-0" />
                    ) : (
                        <div className={`w-7 h-7 flex-shrink-0 ${iconBg} rounded-md
                            flex items-center justify-center`}>
                            <div className={iconColor}>{icon}</div>
                        </div>
                    )}
                    {isLoading ? (
                        <Skeleton className="h-4 w-28 rounded" />
                    ) : (
                        <span className="text-ui-lg font-semibold text-gray-700 dark:text-gray-200 truncate">
                            {title}
                        </span>
                    )}
                </div>

                {/* ── Row 2: value (left) + sparkline/zero-state (right) ── */}
                <div className="flex items-center justify-between gap-3">
                    {isLoading ? (
                        <Skeleton className="h-9 w-20 rounded" />
                    ) : (
                        <p className="text-display-sm font-bold text-content dark:text-white leading-none tracking-tight">
                            {value}
                        </p>                    )}

                    {/* Only drawn when there is a real series.
                        All four of these were manufactured from the single
                        current value — `[0, d*0.3, d*0.5, d*0.75, d]` and, for
                        Total Campaigns, the literal `[0,1,2,1,3,n]`. Whatever
                        actually happened, the curve always rose.

                        The dashed-line fallback went with them: a flat line
                        with a solid endpoint dot reads as "measured, and flat",
                        which is a different claim from "not measured". The
                        dashboard endpoint returns no per-metric history, so
                        nothing is drawn until one exists. */}
                    {!isLoading && hasSpark && (
                        <Sparkline data={sparkData} color={accentHex} />
                    )}
                </div>

                {/* ── Row 3: footer label ── */}
                <div className="flex items-center gap-2 mt-3 pt-2.5 border-t border-line-subtle dark:border-dark-700/60">
                    {isLoading ? (
                        <Skeleton className="h-3 w-24 rounded" />
                    ) : (
                        <>
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                style={{ backgroundColor: accentHex, opacity: 1 }} />
                            <span className="text-ui font-medium text-content-secondary dark:text-gray-300 truncate">
                                {trendLabel}
                            </span>
                            {trend != null && trend !== 0 && (
                                <span className={`ml-auto flex-shrink-0 inline-flex items-center gap-0.5
                                    text-[12px] font-bold
                                    ${hasPositiveTrend
                                        ? "text-emerald-500 dark:text-emerald-400"
                                        : "text-red-500 dark:text-red-400"}`}>
                                    {hasPositiveTrend
                                        ? <ArrowUpRight className="w-3.5 h-3.5" />
                                        : <ArrowDownRight className="w-3.5 h-3.5" />}
                                    {Math.abs(trend).toFixed(1)}%
                                </span>
                            )}
                        </>
                    )}
                </div>

            </div>
        </div>
    );
};

/* ─────────── Empty State ─────────── */
/**
 * Adapter over the shared `EmptyState`.
 *
 * This was a fourth independent empty-state implementation (alongside the
 * templates, support and ad-hoc ones), with its own icon tile, its own type
 * sizes and its own button styling. The dashboard widgets that use it keep
 * their prop signature; only the rendering is now shared, so a dashboard
 * widget with no data looks like every other empty state in the app.
 */
interface EmptyStateProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    actionLabel?: string;
    onAction?: () => void;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
    icon,
    title,
    description,
    actionLabel,
    onAction,
}) => (
    <SharedEmptyState
        bare
        size="sm"
        icon={() => <>{icon}</>}
        title={title}
        description={description}
        primaryAction={
            actionLabel && onAction
                ? { label: actionLabel, onClick: onAction, icon: <Plus className="w-4 h-4" /> }
                : null
        }
    />
);

/* ─────────── KPI Grid ─────────── */
interface DashboardKPIGridProps {
    dashboardData: any;
    isLoading: boolean;
    numberOfCampaigns?: number;
    funnelData?: any;
    recentCampaignStats?: {
        opened_rate?: number;
        spam_rate?: number;
        unsubscribed_rate?: number;
        total_links_clicked?: number;
    } | null;
    campaignTrends?: {
        campaignsTrend?: number | null;
        deliveredTrend?: number | null;
        openRateTrend?: number | null;
        clickRateTrend?: number | null;
    };
}

export const DashboardKPIGrid: React.FC<DashboardKPIGridProps> = ({ isLoading, numberOfCampaigns = 0, funnelData, recentCampaignStats, campaignTrends }) => {
    const emailsDelivered = funnelData?.delivered ?? 0;
    const uniqueOpens = funnelData?.unique_opens ?? 0;
    const uniqueClicks = funnelData?.unique_clicks ?? 0;

    const openRate = emailsDelivered > 0 ? (uniqueOpens / emailsDelivered) * 100 : 0;
    const clickRate = emailsDelivered > 0 ? (uniqueClicks / emailsDelivered) * 100 : 0;

    const fmt = (v: number, s = "") => `${v.toFixed(1)}${s}`;
    const fmtN = (v: number) => v.toLocaleString();

    // When delivered=0, show raw counts rather than meaningless 0% rates
    const displayedDelivered = emailsDelivered > 0 ? fmtN(emailsDelivered) : uniqueOpens > 0 || uniqueClicks > 0 ? "N/A" : "0";
    const displayedOpenRate  = emailsDelivered > 0 ? fmt(openRate, "%") : uniqueOpens > 0 ? `${fmtN(uniqueOpens)} opens` : "0%";
    const displayedClickRate = emailsDelivered > 0 ? fmt(clickRate, "%") : uniqueClicks > 0 ? `${fmtN(uniqueClicks)} clicks` : "0%";

    const kpiCards = useMemo(() => [
        {
            title: "Total Campaigns",
            value: numberOfCampaigns > 0 ? numberOfCampaigns : "0",
            icon: <LayoutGrid className="w-4 h-4" />,
            trend: campaignTrends?.campaignsTrend ?? null,
            trendLabel: "All time",
            accentColor: "bg-gradient-to-r from-blue-500 to-indigo-500",
            accentHex: "#6366f1",
            iconBg: "bg-blue-50 dark:bg-blue-900/20",
            iconColor: "text-blue-600 dark:text-blue-400",
        },
        {
            title: "Emails Delivered",
            value: displayedDelivered,
            icon: <Send className="w-4 h-4" />,
            trend: campaignTrends?.deliveredTrend ?? null,
            trendLabel: emailsDelivered > 0 ? "Total delivered" : uniqueClicks > 0 || uniqueOpens > 0 ? "Engagement tracked" : "No deliveries yet",
            accentColor: "bg-gradient-to-r from-emerald-400 to-teal-500",
            accentHex: "#10b981",
            iconBg: "bg-emerald-50 dark:bg-emerald-900/20",
            iconColor: "text-emerald-600 dark:text-emerald-400",
        },
        {
            title: "Open Rate",
            value: displayedOpenRate,
            icon: <MailOpen className="w-4 h-4" />,
            trend: campaignTrends?.openRateTrend ?? null,
            trendLabel: recentCampaignStats?.opened_rate != null
                ? `${recentCampaignStats.opened_rate.toFixed(1)}% last campaign`
                : "Engagement",
            accentColor: "bg-gradient-to-r from-violet-500 to-purple-500",
            accentHex: "#8b5cf6",
            iconBg: "bg-violet-50 dark:bg-violet-900/20",
            iconColor: "text-violet-600 dark:text-violet-400",
        },
        {
            title: "Click Rate",
            value: displayedClickRate,
            icon: <MousePointerClick className="w-4 h-4" />,
            trend: campaignTrends?.clickRateTrend ?? null,
            trendLabel: recentCampaignStats?.total_links_clicked != null
                ? `${recentCampaignStats.total_links_clicked.toFixed(1)}% last campaign`
                : "Click-through",
            accentColor: "bg-gradient-to-r from-amber-400 to-orange-500",
            accentHex: "#f59e0b",
            iconBg: "bg-amber-50 dark:bg-amber-900/20",
            iconColor: "text-amber-600 dark:text-amber-400",
        },
    ], [numberOfCampaigns, emailsDelivered, openRate, clickRate, campaignTrends]);

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {kpiCards.map((kpi, i) => (
                <KPICard key={i} {...kpi} isLoading={isLoading} animDelay={i * 50} />
            ))}
        </div>
    );
};
